import {
	Box,
	Card,
	IconButton,
	InputAdornment,
	InputLabel,
	ListSubheader,
	MenuItem,
	Select,
	styled,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import React, { Dispatch, forwardRef, SetStateAction, useState } from "react";
import { CustomSwitch, LargeSwitch } from "./custom-switch";
import { SimpleTooltip } from "./tooltip";
import { AlertIcon } from "./svgIcons";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { TFunction } from "i18next";

interface UserInfoTextInputProps {
	label: string;
	description?: string;
	inputType: string;
	value: string;
	setValue: Dispatch<SetStateAction<string>>;
	required?: boolean;
	disabled?: boolean;
}

function UserInfoTextInput(props: UserInfoTextInputProps): JSX.Element {
	const label: React.ReactNode = props.required ? (
		<label>
			{props.label}
			<span style={{ color: "red" }}> * </span>
			{props.description && (
				<div>
					<small className="input-description">{props.description}</small>
				</div>
			)}
		</label>
	) : (
		<label>
			{props.label}
			{props.description && (
				<div>
					<small className="input-description">{props.description}</small>
				</div>
			)}
		</label>
	);

	return (
		<div style={{ display: "flex", columnGap: "20px", alignItems: "center" }}>
			{label}

			<input
				type={props.inputType}
				value={props.value}
				onChange={(e) => {
					let value = e.target.value;
					//formatting
					if (props.inputType === "number") {
						value = value.replace(/^0+/, "") || "0"; // Ensure at least '0' is returned
					}
					props.setValue(value);
				}}
				maxLength={255}
				required={props.required}
				disabled={Boolean(props.disabled)}
			/>
		</div>
	);
}

interface CustomTextInputProps {
	id: string;
	label?: string;
	description?: string;
	placeholder?: string;
	value: string;
	setValue: (value: string) => void;
	isTextArea?: boolean;
	required?: boolean;
	disabled?: boolean;
	type?: string;
	errorMsg?: string;
	setErrorMsg?: (msg: string) => void;
	additinoalInfo?: React.ReactNode;
	onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
	maxLength?: number;
	flexDirection?: "row" | "row-reverse" | "column" | "column-reverse";
}
const CustomTextInput = forwardRef<HTMLInputElement, CustomTextInputProps>(
	(props, ref): JSX.Element => {
		const [showPassword, setShowPassword] = useState<boolean>(false);
		const originalInputType = props.type || "text";
		const direction = props.flexDirection || "column";

		const label: React.ReactNode = (
			<div className="common-text-input-label">
				<InputLabel
					id={props.id + "-label"}
					sx={{
						color: "black",
						fontFamily: "Lexend, Segoe UI, Roboto, sans-serif",
					}}
				>
					{props.label}
				</InputLabel>
				{props.description && (
					<SimpleTooltip
						id={props.id + "-tooltip"}
						description={props.description}
						arrow
						placement="top"
					/>
				)}
			</div>
		);
		const maxLength = props.maxLength
			? props.maxLength
			: props.isTextArea
				? 200
				: 100;

		return (
			<div style={{ display: "flex", flexDirection: direction, gap: "5px" }}>
				{label}
				<TextField
					id={props.id}
					variant="outlined"
					type={
						originalInputType === "password" && showPassword
							? "text"
							: originalInputType
					}
					value={props.value}
					onChange={(e) => {
						props.setValue(e.target.value);
						props.setErrorMsg?.("");
					}}
					onKeyDown={props.onKeyDown}
					inputRef={ref} // 👈 This is important
					inputProps={{
						maxLength: maxLength,
					}}
					InputProps={{
						endAdornment:
							originalInputType === "password" ? (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										tabIndex={-1}
										onClick={() => setShowPassword(!showPassword)}
										sx={{ color: NeutralColor.NEUTRAL_GREY_200 }}
										edge="end"
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							) : null,
					}}
					multiline={props.isTextArea}
					rows={props.isTextArea ? 4 : 1}
					placeholder={props.placeholder}
					required={props.required}
					disabled={Boolean(props.disabled)}
					sx={{
						"& .MuiOutlinedInput-root": {
							borderRadius: "12px",
							"& fieldset": {
								borderColor: props.errorMsg
									? SemanticColor.ERROR_MEDIUM
									: NeutralColor.NEUTRAL_DARK,
								borderRadius: "12px",
							},
							"&:not(.Mui-disabled):hover fieldset": {
								borderColor: PrimaryColor.PRIMARY_LV5,
							},
						},
						"& .MuiInputBase-input": {
							padding: "11px 16px",
						},
					}}
				/>
				{props.errorMsg && (
					<div
						style={{
							display: "flex",
							gap: "5px",
							color: SemanticColor.ERROR,
							alignItems: "center",
						}}
					>
						<AlertIcon />
						{props.errorMsg}
					</div>
				)}
				{props.additinoalInfo}
			</div>
		);
	},
);

interface CustomNumberInputProps {
	id: string;
	label?: string;
	description?: string;
	placeholder?: string;
	value: number;
	setValue: Dispatch<SetStateAction<number>>;
	required?: boolean;
	disabled?: boolean;
	errorMsg?: string;
	setErrorMsg?: (msg: string) => void;

	maxDigit?: number;
	flexDirection?: "row" | "row-reverse" | "column" | "column-reverse";
}

function CustomNumberInput(props: CustomNumberInputProps): JSX.Element {
	const label: React.ReactNode = (
		<div className="common-text-input-label">
			<InputLabel
				id={props.id + "-label"}
				sx={{
					color: "black", // Change the color
					fontFamily: "Lexend, Segoe UI, Roboto, sans-serif", // Change the font family
				}}
			>
				{props.label}
			</InputLabel>
			{props.description && (
				<SimpleTooltip
					id={props.id + "-tooltip"}
					description={props.description}
					arrow
					placement="top"
				></SimpleTooltip>
			)}
		</div>
	);
	const maxDigit = props.maxDigit || 9;
	const MIN_VALUE = 0;
	const MAX_VALUE = 10 ** maxDigit - 1;
	const direction = props.flexDirection || "column";

	return (
		<div style={{ display: "flex", flexDirection: direction, gap: "5px" }}>
			{label}
			<TextField
				id={props.id}
				variant="outlined"
				type="number"
				value={String(props.value)}
				onChange={(e) => {
					let value = e.target.value;
					value = value.replace(/^0+/, "") || "0"; // Ensure at least '0' is returned
					const number = Number(value);
					if (!isNaN(number) && MIN_VALUE <= number && number <= MAX_VALUE) {
						props.setValue(Math.round(number * 10) / 10);
					}
					if (props.setErrorMsg) {
						props.setErrorMsg("");
					}
				}}
				InputProps={{
					inputProps: {
						min: MIN_VALUE, // Set minimum value
						max: MAX_VALUE, // Set maximum value
					},
				}}
				placeholder={props.placeholder}
				required={props.required}
				disabled={Boolean(props.disabled)}
				sx={{
					"& .MuiOutlinedInput-root": {
						borderRadius: "12px", // Change the border-radius here

						"& fieldset": {
							borderColor: props.errorMsg
								? SemanticColor.ERROR_MEDIUM
								: NeutralColor.NEUTRAL_DARK, // Set border color
							borderRadius: "12px",
						},
					},
					"&:hover fieldset": {
						borderColor: PrimaryColor.PRIMARY_LV5, // Change to your desired color on hover
					},
					"& .MuiInputBase-input": {
						padding: "11px 16px", // Change the padding as needed
					},
				}}
			/>
			{props.errorMsg && props.errorMsg.length > 0 && (
				<div
					style={{
						display: "flex",
						gap: "5px",
						color: SemanticColor.ERROR,
						alignItems: "center",
					}}
				>
					<AlertIcon></AlertIcon>
					{props.errorMsg}
				</div>
			)}
		</div>
	);
}

interface DropdownProps {
	id: string;
	label: string;
	description?: string;
	options: Array<{
		title: string;
		value: any;
		onClick?: () => void;
		renderValue?: string;
	}>;
	value: any;
	setValue: (value: any) => void;
	className?: string;
	labelClassName?: string;
	labelDescriptionColor?: string;
	required?: boolean;
	noBorder?: boolean;
	displayEmpty?: boolean;
	disabled?: boolean;
	hasError?: boolean;
}

function CustomDropdown(props: DropdownProps): JSX.Element {
	const handleChange = (event: any) => {
		props.setValue(event.target.value);
	};

	return (
		<div className={`common-dropdown-container ${props.className || ""}`}>
			<div style={{ padding: "0px 16px" }}>
				<InputLabel
					id={props.id + "-label"}
					className={props.labelClassName}
					sx={{
						color: "black", // Change the color
						fontFamily: "Lexend, Segoe UI, Roboto, sans-serif", // Change the font family
					}}
				>
					{props.label}
				</InputLabel>
			</div>

			<Select
				labelId={props.id + "-label"}
				id={props.id}
				value={props.value}
				disabled={Boolean(props.disabled)}
				onChange={handleChange}
				IconComponent={KeyboardArrowDownIcon} // Toggle icon based on open state
				renderValue={(selected) => {
					// Return the label corresponding to the selected value
					const selectedOption = props.options.find(
						(option) => option.value === selected,
					);
					return selectedOption
						? selectedOption.renderValue || selectedOption.title
						: "";
				}}
				sx={{
					width: "100%",
					".MuiSelect-select": {
						padding: "11px 16px", // Adjust padding for the input area (where selected value is shown)
					},

					"& .MuiOutlinedInput-notchedOutline": {
						border: props.noBorder ? "none" : null,
						borderColor: props.hasError
							? SemanticColor.ERROR
							: NeutralColor.NEUTRAL_DARK, // Set border color
					},
					marginTop: props.label ? 0 : "23px",
					borderRadius: "12px", // Change the border radius
				}}
				MenuProps={{
					PaperProps: {
						sx: {
							borderRadius: "12px",
							overflow: "hidden",
						},
					},
				}}
				displayEmpty={props.displayEmpty}
			>
				{props.options.map((entry, index) => {
					return (
						<MenuItem
							key={index}
							value={entry.value}
							onClick={entry.onClick}
							sx={{
								margin: "0px 8px",
								padding: "8px 16px",
								borderRadius: "10px",
							}}
						>
							{entry.title}
						</MenuItem>
					);
				})}
			</Select>
		</div>
	);
}

function RowDropdown(props: DropdownProps): JSX.Element {
	// const [icon, setIcon] = useState<any>(KeyboardArrowDownIcon); // State to manage whether the select is open

	const handleChange = (event: any) => {
		props.setValue(event.target.value);
	};
	// const handleOpen = () => {
	// 	setIcon(KeyboardArrowUpIcon);
	// };

	// const handleClose = () => {
	// 	setIcon(KeyboardArrowDownIcon);
	// };

	return (
		<div className={`common-row-dropdown-container ${props.className || ""}`}>
			<div className={props.labelClassName}>
				<div>{props.label}</div>
				{props.description && (
					<div
						className={`typography-caption`}
						style={{
							color:
								props.labelDescriptionColor || NeutralColor.NEUTRAL_GREY_600,
						}}
					>
						{props.description}
					</div>
				)}
			</div>

			<Select
				labelId={props.id + "-label"}
				id={props.id}
				value={props.value}
				onChange={handleChange}
				// onOpen={handleOpen}
				// onClose={handleClose}
				IconComponent={KeyboardArrowDownIcon} // Toggle icon based on open state
				renderValue={(selected) => {
					// Return the label corresponding to the selected value
					const selectedOption = props.options.find(
						(option) => option.value === selected,
					);
					return selectedOption
						? selectedOption.renderValue || selectedOption.title
						: "";
				}}
				sx={{
					".MuiSelect-select": {
						padding: "11px 16px", // Adjust padding for the input area (where selected value is shown)
					},
					"& .MuiOutlinedInput-notchedOutline": {
						border: props.noBorder ? "none" : null,
						borderColor: props.hasError
							? SemanticColor.ERROR
							: NeutralColor.NEUTRAL_DARK, // Set border color
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: PrimaryColor.PRIMARY_LV5, // Set border color
					},

					marginTop: props.label ? 0 : "23px",
					borderRadius: "12px", // Change the border radius
					overflow: "hidden",
				}}
				MenuProps={{
					PaperProps: {
						sx: {
							borderRadius: "12px",
							overflow: "hidden",
						},
					},
				}}
				displayEmpty={props.displayEmpty}
			>
				{props.options.map((entry, index) => {
					return (
						<MenuItem
							key={index}
							value={entry.value}
							onClick={entry.onClick}
							sx={{
								margin: "0px 8px",
								padding: "8px 16px",
								borderRadius: "10px",
							}}
						>
							{entry.title}
						</MenuItem>
					);
				})}
			</Select>
		</div>
	);
}

function TableDropdown(props: DropdownProps): JSX.Element {
	const handleChange = (event: any) => {
		props.setValue(event.target.value);
	};

	return (
		<div className={`common-dropdown-container ${props.className || ""}`}>
			{/* <div style={{ padding: "0px 16px" }}>
				<InputLabel
					id={props.id + "-label"}
					className={props.labelClassName}
					sx={{
						color: "black", // Change the color
						fontFamily: "Lexend, Segoe UI, Roboto, sans-serif", // Change the font family
					}}
				>
					{props.label}
				</InputLabel>
			</div> */}

			<Select
				labelId={props.id + "-label"}
				id={props.id}
				value={props.value}
				disabled={Boolean(props.disabled)}
				onChange={handleChange}
				IconComponent={KeyboardArrowDownIcon} // Toggle icon based on open state
				renderValue={(selected) => {
					// Return the label corresponding to the selected value
					const selectedOption = props.options.find(
						(option) => option.value === selected
					);
					return selectedOption
						? selectedOption.renderValue || selectedOption.title
						: "";
				}}
				sx={{
					".MuiSelect-select": {
						padding: "11px 16px", // Adjust padding for the input area (where selected value is shown)
					},
					"& .MuiOutlinedInput-notchedOutline": {
						border: props.noBorder ? "none" : null,
						borderColor: NeutralColor.NEUTRAL_DARK, // Set border color
					},
					marginTop: "5px",
					borderRadius: "12px", // Change the border radius
				}}
				MenuProps={{
					PaperProps: {
						sx: {
							borderRadius: "12px",
							overflow: "hidden",
						},
					},
				}}
				displayEmpty={props.displayEmpty}
			>
				{props.options.map((entry, index) => {
					return (
						<MenuItem
							key={index}
							value={entry.value}
							onClick={entry.onClick}
							sx={{
								margin: "0px 8px",
								padding: "8px 16px",
								borderRadius: "10px",
							}}
						>
							{entry.title}
						</MenuItem>
					);
				})}
			</Select>
		</div>
	);
}

interface CheckboxProps {
	label: string;
	description?: string;
	className?: string;
	labelClassName?: string;
	checked: boolean;
	setChecked: Dispatch<SetStateAction<boolean>>;
}

function CustomCheckbox(props: CheckboxProps): JSX.Element {
	const labelClassName = props.labelClassName || "typography-body-02";
	return (
		<label className={props.className}>
			<input
				type={"checkbox"}
				checked={props.checked}
				onChange={(e) => {
					props.setChecked(!props.checked);
				}}
				style={{ display: "none" }}
			/>
			<div
				className={`custom-checkbox ${
					props.checked ? "custom-checkbox-checked" : ""
				}`}
			></div>
			<div className={labelClassName}>{props.label}</div>

			{props.description && (
				<div>
					<small className="input-description">{props.description}</small>
				</div>
			)}
		</label>
	);
}

interface SearchBarProps {
	id: string;
	placeholder: string;
	searchButtonText: string;
	searchText: string;
	setSearchText: Dispatch<SetStateAction<string>>;
	onSearch: (searchText: string) => void;
}

function CustomSearchBar(props: SearchBarProps): JSX.Element {
	const { searchText, setSearchText } = props;
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(event.target.value);
	};
	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			props.onSearch(searchText);
		}
	};

	return (
		<TextField
			id={props.id}
			variant="outlined"
			type="text"
			value={searchText}
			onChange={handleChange}
			onKeyUp={handleKeyPress}
			inputProps={{
				maxLength: 100,
			}}
			InputProps={{
				startAdornment: (
					<InputAdornment position="start">
						<SearchIcon />
					</InputAdornment>
				),
			}}
			placeholder={props.placeholder}
			sx={{
				"& .MuiOutlinedInput-root": {
					"& fieldset": {
						borderColor: NeutralColor.NEUTRAL_DARK, // Set border color
						borderRadius: "12px",
					},
					"& .MuiInputBase-input": {
						padding: "12px 0px", // Adjust padding inside the input element
					},
				},
			}}
		/>
	);
}

interface LabeledSwitchProps {
	label: string;
	labelDescription?: string;
	labelTypographyClass?: string;
	uncheckedLabel: string;
	checkedLabel: string;
	checked: boolean;
	setChecked: Dispatch<SetStateAction<boolean>>;
}

function LabeledSwitch(props: LabeledSwitchProps): JSX.Element {
	const label: React.ReactNode = (
		<div>
			<label className={`${props.labelTypographyClass}`}>{props.label}</label>
			<div
				style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
				className="typography-caption"
			>
				{props.labelDescription}
			</div>
		</div>
	);
	return (
		<div
			style={{
				display: "flex",
				columnGap: "20px",
				alignItems: "center",
				flexWrap: "wrap",
			}}
		>
			{label}

			<Box sx={{ position: "relative", display: "inline-block" }}>
				<LargeSwitch
					checked={props.checked}
					onChange={(e) => {
						props.setChecked(!props.checked);
					}}
					color="primary"
				/>
				<Box
					sx={{
						position: "absolute",
						left: "50%",
						width: "100%",
						display: "flex",
						justifyContent: "space-around",
						top: "50%",
						transform: "translate(-50%, -50%)",
						pointerEvents: "none", // Prevent interaction with text
					}}
				>
					<Typography
						sx={{
							color: props.checked ? NeutralColor.NEUTRAL_GREY_300 : "white",
						}}
					>
						{props.uncheckedLabel}
					</Typography>
					<Typography
						sx={{
							color: props.checked
								? NeutralColor.NEUTRAL_GREY_950
								: NeutralColor.NEUTRAL_GREY_950,
						}}
					>
						{props.checkedLabel}
					</Typography>
				</Box>
			</Box>
		</div>
	);
}

interface CommonSwitchProps {
	id: string;
	label?: string;
	labelTypographyClass?: string;
	checked: boolean;
	disabled?: boolean;
	setChecked: (checked: boolean) => void;
}

function CommonSwitch(props: CommonSwitchProps): JSX.Element {
	const label: React.ReactNode = props.label ? (
		<label className={`${props.labelTypographyClass}`}>{props.label}</label>
	) : null;
	return (
		<div style={{ display: "flex", columnGap: "20px", alignItems: "center" }}>
			{label}
			<Box sx={{ position: "relative", display: "inline-block" }}>
				<CustomSwitch
					id={props.id}
					checked={props.checked}
					onChange={(e) => {
						props.setChecked(!props.checked);
					}}
					color="primary"
					disabled={Boolean(props.disabled)}
				/>
			</Box>
		</div>
	);
}

interface RadioGroupProps {
	options: Array<{
		name: string;
		description?: string;
		value: any;
		children?: React.ReactNode;
		key: string;
	}>;
	selectedOption: any;
	onChange: (event: any) => void;
	className?: string;
	disabled?: boolean;
}
function CardRadioGroup(props: RadioGroupProps): JSX.Element {
	//hide default radio, customize look
	const radios: Array<React.ReactNode> = props.options.map((option, index) => {
		const checked: boolean = props.selectedOption === option.value;
		return (
			<label key={option.key} style={{ cursor: "pointer", height: "100%" }}>
				<input
					type="radio"
					name={option.name}
					value={option.value}
					checked={checked}
					onChange={props.onChange}
					style={{ display: "none" }}
					disabled={props.disabled}
				/>
				<div
					className={`card-radio-wrapper ${
						checked ? "card-radio-wrapper-checked" : ""
					}`}
					tabIndex={0}
				>
					<div
						className={`card-radio-button ${
							checked ? "card-radio-button-checked" : ""
						}`}
					>
						<div className="typography-heading-03">{option.name}</div>
						{option.description && (
							<div
								className="typography-body-02"
								style={{ textAlign: "center" }}
							>
								{option.description}
							</div>
						)}
					</div>
				</div>
			</label>
		);
	});
	return (
		<div
			className={props.className}
			style={{
				display: "grid",
				gridAutoRows: "1fr",
				rowGap: "20px",
				alignItems: "center",
			}}
		>
			{radios}
		</div>
	);
}

function StandardRadioGroup(props: RadioGroupProps): JSX.Element {
	//hide default radio, customize look
	const radios: Array<React.ReactNode> = props.options.map((option, index) => {
		const checked: boolean = props.selectedOption === option.value;
		return (
			<label
				key={option.key}
				style={{
					display: "flex",
					cursor: props.disabled ? "not-allowed" : "pointer",
					columnGap: "5px",
					alignItems: "center",
					minWidth: "85px",
				}}
			>
				<input
					type="radio"
					name={option.name}
					value={option.value}
					checked={checked}
					onChange={props.onChange}
					style={{ display: "none" }}
					disabled={props.disabled}
				/>

				<div
					className={`custom-checkbox ${
						checked ? "custom-checkbox-checked" : ""
					}  
					${props.disabled ? "custom-checkbox-disabled" : ""}
`}
				></div>
				<div className="typography-body-02">{option.name}</div>
			</label>
		);
	});
	return (
		<div
			className={props.className}
			style={{
				display: "flex",

				rowGap: "20px",
				alignItems: "center",
			}}
		>
			{radios}
		</div>
	);
}

function CustomRadioGroup(props: RadioGroupProps): JSX.Element {
	//hide default radio, customize look
	const radios: Array<JSX.Element> = props.options.map((option) => {
		const checked: boolean = props.selectedOption === option.value;

		return (
			<label
				key={option.key}
				style={{ cursor: "pointer", height: "100%", width: "100%" }}
			>
				<input
					type="radio"
					name={option.name}
					value={option.value}
					checked={checked}
					onChange={props.onChange}
					style={{ display: "none" }}
					disabled={props.disabled}
				/>
				{option.children}
			</label>
		);
	});
	return <div className={props.className}>{radios}</div>;
}

interface RangeInputGroupProps<T extends string> {
	label: string;
	minValue: string;
	setMinValue: (minValue: string) => void;
	maxValue: string;
	setMaxValue: (maxValue: string) => void;

	unitId: string;
	setUnitId: (unitId: string) => void;
	unitOptions: Array<{ unitId: string; unit: string }>; // Accept enum or array
	mandatory: boolean;
	translation: TFunction<"translation", undefined>;
}

function RangeInputGroup<T extends string>(
	props: RangeInputGroupProps<T>,
): JSX.Element {
	const {
		label,
		minValue,
		maxValue,
		setMinValue,
		setMaxValue,
		unitId,
		setUnitId,
		unitOptions,
		mandatory,
		translation,
	} = props;

	const minNumber = parseFloat(minValue);
	const maxNumber = parseFloat(maxValue);

	const isMinValid = !isNaN(minNumber);
	const isMaxValid = !isNaN(maxNumber);

	// Add checks for required inputs
	const isMinEmpty = minValue.trim() === "";
	const isMaxEmpty = maxValue.trim() === "";

	return (
		<Box
			display="grid"
			gridTemplateColumns={"1fr 3fr 3fr 2fr"}
			alignItems="center"
			mb={2}
		>
			<Typography>
				{label}
				{mandatory ? <span style={{ color: "red" }}> *</span> : null}
			</Typography>

			<TextField
				variant="outlined"
				placeholder="Min"
				type="number"
				value={minValue}
				onChange={(e) => setMinValue(e.target.value)}
				sx={{ mx: 0.5 }}
				error={
					mandatory ? isMinEmpty || !isMinValid : !isMinValid && !isMinEmpty
				}
				helperText={
					mandatory && isMinEmpty
						? translation("RequiredField")
						: !isMinValid && !isMinEmpty
							? translation("InvalidNumber")
							: ""
				}
			/>

			<TextField
				variant="outlined"
				placeholder="Max"
				type="number"
				value={maxValue}
				onChange={(e) => setMaxValue(e.target.value)}
				sx={{ mx: 0.5 }}
				error={
					mandatory
						? isMaxEmpty ||
							!isMaxValid ||
							(isMinValid && isMaxValid && minNumber > maxNumber)
						: (!isMaxValid && !isMaxEmpty) ||
							(isMinValid && isMaxValid && minNumber > maxNumber)
				}
				helperText={
					mandatory && isMaxEmpty
						? translation("RequiredField")
						: !isMaxValid && !isMaxEmpty
							? translation("InvalidNumber")
							: isMinValid && isMaxValid && minNumber > maxNumber
								? translation("MaxLessThanMin")
								: ""
				}
			/>

			<Select
				value={unitId}
				onChange={(e) => setUnitId(e.target.value)}
				sx={{ mx: 0.5 }}
			>
				{unitOptions.map((option) => (
					<MenuItem key={option.unitId} value={option.unitId}>
						{option.unit}
					</MenuItem>
				))}
			</Select>
		</Box>
	);
}
export {
	UserInfoTextInput,
	CustomDropdown,
	TableDropdown,
	CustomCheckbox,
	CustomSearchBar,
	CustomTextInput,
	CustomNumberInput,
	LabeledSwitch,
	CardRadioGroup,
	StandardRadioGroup,
	CommonSwitch,
	CustomRadioGroup,
	RowDropdown,
	RangeInputGroup,
};
